'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Upload, X, Play } from 'lucide-react';
import { useImageUpload } from '@/hooks/useImageUpload';
import { useUpdateBusinessMutation } from '@/api/mutations/updateBusiness';
import { useUpdateEducationMutation } from '@/api/mutations/updateEducation';
import { toast } from 'sonner';
import Image from 'next/image';

const MAX_GALLERY_ITEMS = 10;
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'm4v', 'ogg'];
const ACCEPT = 'image/*,video/*';

interface BusinessGalleryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  business: any;
  canEdit: boolean;
  entityType?: 'business' | 'education';
}

const isVideo = (url: string) => {
  const clean = url.split('?')[0].toLowerCase();
  const ext = clean.split('.').pop();
  return ext ? VIDEO_EXTENSIONS.includes(ext) : false;
};

export function BusinessGalleryDialog({
  open,
  onOpenChange,
  business,
  canEdit,
  entityType = 'business',
}: BusinessGalleryDialogProps) {
  const [items, setItems] = useState<string[]>(business.gallery || []);
  const [isUploading, setIsUploading] = useState(false);
  const [previewItem, setPreviewItem] = useState<string | null>(null);

  const isEducation = entityType === 'education';
  const { uploadImage } = useImageUpload({
    purpose: 'businesses',
  });
  const updateBusinessMutation = useUpdateBusinessMutation(business.id);
  const updateEducationMutation = useUpdateEducationMutation(business.id);

  useEffect(() => {
    setItems(business.gallery || []);
  }, [business.gallery]);

  const persist = async (next: string[]) => {
    if (isEducation) {
      await updateEducationMutation.mutateAsync({
        gallery: next,
        name: business.name,
      } as any);
    } else {
      await updateBusinessMutation.mutateAsync({
        gallery: next,
        name: business.name,
        description: business.description,
      } as any);
    }
  };

  const handleFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    if (items.length + files.length > MAX_GALLERY_ITEMS) {
      toast.error(`Maximum ${MAX_GALLERY_ITEMS} items allowed`);
      return;
    }

    setIsUploading(true);
    try {
      const uploadedKeys: string[] = [];
      for (const file of files) {
        const key = await uploadImage(file);
        if (key) uploadedKeys.push(key);
      }
      const next = [...items, ...uploadedKeys];
      setItems(next);
      await persist(next);
    } catch {
      toast.error('Failed to upload media');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = async (index: number) => {
    const previous = items;
    const next = items.filter((_, i) => i !== index);
    setItems(next);
    try {
      await persist(next);
    } catch {
      toast.error('Failed to remove item');
      setItems(previous);
    }
  };

  const remaining = MAX_GALLERY_ITEMS - items.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-3xl rounded-2xl sm:rounded-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Gallery {canEdit && `(${items.length}/${MAX_GALLERY_ITEMS})`}
          </DialogTitle>
        </DialogHeader>

        {items.length === 0 && !canEdit && (
          <div className="text-center py-10 text-gray-500">
            No gallery items yet
          </div>
        )}

        {items.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {items.map((item, index) => (
              <div
                key={`${item}-${index}`}
                className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group bg-gray-100"
              >
                <button
                  type="button"
                  onClick={() => setPreviewItem(item)}
                  aria-label={`View ${isVideo(item) ? 'video' : 'image'} ${
                    index + 1
                  } of ${items.length}`}
                  className="absolute inset-0 h-full w-full cursor-pointer"
                >
                  {isVideo(item) ? (
                    <>
                      <video
                        src={item}
                        className="w-full h-full object-cover pointer-events-none"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                        <Play className="h-10 w-10 text-white" aria-hidden="true" />
                      </div>
                    </>
                  ) : (
                    <Image
                      src={item}
                      alt=""
                      fill
                      sizes="(max-width: 768px) 50vw, 25vw"
                      className="object-cover pointer-events-none"
                    />
                  )}
                </button>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    className="absolute top-2 right-2 h-7 w-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
                    aria-label="Remove"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {canEdit && remaining > 0 && (
          <div className="mt-4">
            <label
              htmlFor="business-gallery-upload"
              tabIndex={0}
              role="button"
              aria-label="Click or press enter to upload images or videos"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  document.getElementById('business-gallery-upload')?.click();
                }
              }}
              className="flex flex-col items-center gap-2 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {isUploading ? (
                <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
              ) : (
                <Upload className="h-8 w-8 text-gray-500" />
              )}
              <p className="text-sm text-gray-600">
                {isUploading
                  ? 'Uploading...'
                  : 'Click to upload images or videos'}
              </p>
              <p className="text-xs text-gray-400">
                {remaining} slot{remaining > 1 ? 's' : ''} remaining
              </p>
            </label>
            <input
              id="business-gallery-upload"
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={handleFilesChange}
              disabled={isUploading}
            />
          </div>
        )}

      </DialogContent>
      {previewItem &&
        typeof window !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-2 sm:p-4 cursor-pointer"
            onClick={() => setPreviewItem(null)}
          >
            <button
              type="button"
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-white hover:text-gray-300 z-10 bg-black/40 rounded-full p-1"
              onClick={() => setPreviewItem(null)}
              aria-label="Close preview"
            >
              <X className="h-7 w-7 sm:h-8 sm:w-8" />
            </button>
            {isVideo(previewItem) ? (
              <video
                src={previewItem}
                controls
                autoPlay
                className="max-w-[98vw] max-h-[95vh] w-auto h-auto"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                src={previewItem}
                alt="Preview"
                className="max-w-[98vw] max-h-[95vh] w-auto h-auto object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>,
          document.body
        )}
    </Dialog>
  );
}
